import { useCallback, useEffect, useState } from 'react';
import {
  Loader,
  AlertCircle,
  Clock,
  RefreshCcw,
  MessageSquare,
  Activity,
  KanbanSquare,
  ArrowRightLeft,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { usernameCacheService } from '../../services/usernameCache';
import type { EventTimelineEntry, EventTimelineSource } from '../../types/eventTeam';
import { CreatorProfile } from '../CreatorProfile';
import { cn } from '../../utils/cn';

export interface EventTimelineSectionProps {
  eventId: string;
  /** Bumped by the parent on any event WS message so the timeline auto-refreshes. */
  refreshNonce?: number;
  /** Default 200; max 500 per backend. */
  limit?: number;
}

const SOURCE_META: Record<EventTimelineSource, { label: string; color: string; icon: typeof Activity }> = {
  event_comment: { label: 'Comment', color: 'text-blue-300', icon: MessageSquare },
  event_activity: { label: 'Activity', color: 'text-indigo-300', icon: Activity },
  kanban_activity: { label: 'Kanban activity', color: 'text-accent-300', icon: KanbanSquare },
  kanban_comment: { label: 'Kanban comment', color: 'text-accent-200', icon: MessageSquare },
  kanban_card_history: { label: 'Card moved', color: 'text-teal-300', icon: ArrowRightLeft },
};

/** Best-effort comment body extraction across the union of `payload` shapes. */
function extractCommentBody(entry: EventTimelineEntry): string | null {
  const p = entry.payload;
  if (!p || typeof p !== 'object') return null;
  const obj = p as Record<string, unknown>;
  if (typeof obj.content === 'string') return obj.content;
  if (typeof obj.body === 'string') return obj.body;
  if (typeof obj.message === 'string') return obj.message;
  return null;
}

/**
 * Unified timeline (guide §1.4): merges event comments/activity + linked-card
 * kanban activity/comments/moves, newest first.
 */
export const EventTimelineSection = ({ eventId, refreshNonce = 0, limit = 200 }: EventTimelineSectionProps) => {
  const [entries, setEntries] = useState<EventTimelineEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (initial: boolean) => {
      if (!eventId) return;
      if (initial) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const list = await eventTeamApiService.getEventTimeline(eventId, { limit });
        // Defensive: server says newest-first but normalise just in case.
        const sorted = [...list].sort((a, b) => b.at - a.at);
        setEntries(sorted);
        usernameCacheService.prefetchUsernames(sorted.map((e) => e.actor_id).filter(Boolean));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load timeline');
      } finally {
        if (initial) setLoading(false);
        setRefreshing(false);
      }
    },
    [eventId, limit],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load(false);
  }, [refreshNonce, load]);

  const count = entries?.length ?? 0;
  const cappedHint = count >= limit;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
          <Clock className="w-4 h-4 text-accent-400" />
          Timeline ({count}
          {cappedHint ? '+' : ''})
          {refreshing && <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" />}
        </h3>
        <button
          type="button"
          onClick={() => void load(false)}
          disabled={loading || refreshing}
          className="glass-button-secondary !p-2"
          title="Refresh"
        >
          <RefreshCcw className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="glass-card p-3 border-l-4 border-red-400 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-red-300">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-glass-400 py-3">
          <Loader className="w-4 h-4 animate-spin" />
          Loading timeline…
        </div>
      ) : count === 0 ? (
        <p className="text-sm text-glass-400 py-3 text-center">No timeline entries.</p>
      ) : (
        <div className="space-y-2">
          {(entries ?? []).map((entry) => {
            const meta = SOURCE_META[entry.source] ?? {
              label: entry.source,
              color: 'text-glass-300',
              icon: Activity,
            };
            const Icon = meta.icon;
            const isComment = entry.source === 'event_comment' || entry.source === 'kanban_comment';
            const commentBody = isComment ? extractCommentBody(entry) : null;

            return (
              <div key={`${entry.source}-${entry.id}`} className="rounded-lg border border-glass-200/15 bg-glass-100/5 p-3">
                <div className="flex items-start gap-3">
                  <div className={cn('flex-shrink-0 w-7 h-7 rounded-full bg-glass-200/20 flex items-center justify-center', meta.color)}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full bg-glass-200/10 font-medium', meta.color)}>
                        {meta.label}
                      </span>
                      <span className="text-sm text-glass-200">{entry.summary}</span>
                    </div>
                    {commentBody && (
                      <p className="text-xs text-glass-300 mt-1.5 whitespace-pre-wrap bg-dark-900/40 rounded-lg p-2 border border-glass-200/10">
                        {commentBody}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[11px] text-glass-500">
                      <CreatorProfile creatorId={entry.actor_id} inline />
                      <span>{formatDistanceToNow(new Date(entry.at), { addSuffix: true })}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
